import React from 'react';
import { Button } from 'reactstrap';
import { LuCamera, LuPaperclip, LuX } from 'react-icons/lu';
import { AccentButton } from '../AccentButton';
import { AccentTemplateTextAreaUnbound } from '../unbound/AccentTemplateTextAreaUnbound';
import { accentUtils, t } from '../../services/HelperService';
import { AssistantAttachmentStrip } from './AssistantAttachmentStrip';
import { ATTACHMENT_STATUS_UPLOADING, describeAttachment } from './AttachmentChip';
import { useScreenSnip } from './AccentScreenSnip';
import { showImageMarkup } from './AccentImageMarkupDlg';

const safeString = (v) => (v ?? '').toString();

/** Most screenshots read fine at this size and the upload stays small. */
const MAX_IMAGE_EDGE = 1600;
export const MAX_PROMPT_IMAGES = 4;

/** A text paste at or above this many characters is offered as a session file instead of message text. */
export const LONG_PASTE_CHARS = 1500;

/** What the paperclip picker offers by default — what the session reader takes. */
export const DEFAULT_ATTACH_ACCEPT = '.pdf,.png,.jpg,.jpeg,.gif,.webp,.bmp,.csv,.txt,.md,.json,.yaml,.yml,.xml,.html,.xlsx,.xls';

const hasFiles = (dt) => {
  try {
    return Array.from(dt?.types ?? []).includes('Files') || (dt?.files?.length ?? 0) > 0;
  } catch {
    return false;
  }
};

/**
 * Turns a decoded image source (an Image or a canvas) into the data URL a prompt
 * image is sent as: downscaled to MAX_IMAGE_EDGE, PNG (keeps screenshot text crisp),
 * JPEG only when the PNG would be very large. Shared by the paste path and the
 * preview region snip (AccentRegionSnip). Returns null when drawing fails.
 */
export const toPromptImageDataUrl = (source, sourceWidth = source?.width, sourceHeight = source?.height) => {
  try {
    const w = sourceWidth || 1;
    const h = sourceHeight || 1;
    const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(w, h));
    const width = Math.max(1, Math.round(w * scale));
    const height = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(source, 0, 0, width, height);
    let dataUrl = canvas.toDataURL('image/png');
    if (dataUrl.length > 3_000_000) {
      dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    }
    return dataUrl;
  } catch {
    return null;
  }
};

/** A fresh id for a prompt image entry ({ id, dataUrl }). */
export const newPromptImageId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

/** Shows an image the composer holds as a data URL in a new tab (a data URL cannot be navigated to directly). */
const openDataUrl = (dataUrl) => {
  try {
    const [head, body] = safeString(dataUrl).split(',');
    const mediaType = (head.match(/^data:([^;]+)/) || [])[1] || 'image/png';
    const bytes = atob(body);
    const buffer = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i += 1) buffer[i] = bytes.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([buffer], { type: mediaType }));
    window.open(url, '_blank', 'noopener');
    setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* fine */ } }, 60000);
  } catch { /* nothing to show */ }
};

/**
 * Reads a pasted image file into a downscaled data URL. Resolves null when the file
 * cannot be decoded (never throws into the paste handler). Exported for hosts that
 * take an image paste elsewhere in their dialog and want it read the same way.
 */
export const readPastedImage = (file) => new Promise((resolve) => {
  try {
    const reader = new FileReader();
    reader.onerror = () => resolve(null);
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => resolve(null);
      img.onload = () => resolve(toPromptImageDataUrl(img, img.width, img.height));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  } catch {
    resolve(null);
  }
});

/**
 * Prompt composer (text area + send action(s)).
 *
 * Props:
 * - value
 * - onChange(value)
 * - onSend({ text, intent }): async allowed; called via AccentButton promise pattern.
 *   intent is 'ask' | 'change' when the composer offers the intent toggle, and
 *   undefined on legacy single-button hosts — the AI then infers intent.
 * - askLabel?: when set, a MODE TOGGLE renders — an "ask" segment with this label and
 *   a "change" segment labeled by sendLabel — plus a single Send button. The toggle
 *   selects the intent ('change' by default, sticky across sends); both the Send
 *   button and Enter send with the SELECTED intent. When unset, the single legacy
 *   Send button renders and no intent is sent.
 * - sendLabel?: label of the 'change' toggle segment (and of the single legacy Send
 *   button when askLabel is unset; defaults to 'Send')
 * - defaultIntent?: which toggle segment starts selected ('ask' | 'change';
 *   defaults to 'change'). Chat-first hosts (the Product Author's builder chat)
 *   start on 'ask' so a plain Enter never mutates the definition by surprise.
 * - images? / onAddImage(image) / onRemoveImage(id): when the host supplies these,
 *   screenshots can be PASTED into the composer or SNIPPED from the screen (the camera
 *   button: the browser hands over one frame of the tab — or a window, or the whole
 *   screen — and the user drags over the part to send) — each becomes a downscaled
 *   data URL entry ({ id, dataUrl, keptName? }) shown as a removable chip, marked as
 *   going with the message, and sent with it (see AccentPromptChat). A host that also
 *   keeps the image as a session file sets `keptName` to the file's name once it has
 *   it: the image and the file then show as ONE chip (the file's, with the dot and the
 *   local thumbnail), and its × takes both away. Every image chip offers "Mark up":
 *   a box, an arrow, a pen line or a label drawn on it before it goes. Hosts that
 *   don't pass them keep a text-only composer.
 * - imageHint?: the line under the composer when nothing else needs saying (defaults
 *   to what can be pasted or snipped here).
 * - attachments? / onAttachFiles(files) / onDropText(text) / dropActive?: SESSION FILES.
 *   When the host supplies `attachments` (chip entries, see AttachmentChip) the strip
 *   under the textarea shows them alongside this message's screenshots; files reach
 *   the host by paste, drop (anywhere on the composer; `dropActive` lets the host
 *   light the zone while a drag is over the whole dialog) or the paperclip, and a long
 *   text paste is offered as a session file ("Long paste — keep it as a session file
 *   instead?") through onDropText. onRemoveAttachment / onKeepAttachment(att, fields) /
 *   onConfirmAttachment / onOpenAttachment are the chips' actions; acceptFiles narrows
 *   the picker. Hosts without a session pass none of these and nothing changes.
 * - disabled?
 * - placeholder?
 *
 * Ref (imperative):
 * - send(text, intent): sends the given text as if the user clicked Send — same
 *   onSend flow, same button spinner/self-disable. No-ops when disabled or empty.
 */
export const AccentPromptComposer = React.forwardRef(({
  value,
  onChange,
  onSend,
  disabled,
  label,
  placeholder,
  templateCatalog,
  askLabel,
  sendLabel,
  defaultIntent,
  images,
  onAddImage,
  onRemoveImage,
  imageHint,
  attachments,
  onAttachFiles,
  onDropText,
  dropActive,
  onRemoveAttachment,
  onKeepAttachment,
  onConfirmAttachment,
  onOpenAttachment,
  acceptFiles = DEFAULT_ATTACH_ACCEPT,
  rows = 3,
  max = 4000,
}, ref) => {
  const text = safeString(value);
  // The textarea's maxLength silently truncates a paste, so a message that sits at the
  // cap most likely lost its tail - say so where the user is looking.
  const atMax = Number.isFinite(max) && max > 0 && text.length >= max;
  const imageList = React.useMemo(() => (Array.isArray(images) ? images : []), [images]);
  const hasImages = imageList.length > 0;
  const allowImages = typeof onAddImage === 'function';
  const allowFiles = typeof onAttachFiles === 'function';
  const allowDropText = typeof onDropText === 'function';
  const hasStrip = Array.isArray(attachments);
  const attachmentList = React.useMemo(() => (Array.isArray(attachments) ? attachments.filter(Boolean) : []), [attachments]);

  // Drag state of the composer itself (the host's dropActive covers the dialog).
  const [dragOver, setDragOver] = React.useState(false);
  const dragDepthRef = React.useRef(0);
  const fileInputRef = React.useRef(null);

  // A long text paste: it lands in the textarea as pasted, and this offer sits under it
  // for a while — "keep as file" rewinds the draft to what it was and hands the text to
  // the host as a session file.
  const [longPaste, setLongPaste] = React.useState(null);
  React.useEffect(() => {
    if (!longPaste) return undefined;
    const timer = setTimeout(() => setLongPaste(null), 20000);
    return () => clearTimeout(timer);
  }, [longPaste]);
  React.useEffect(() => {
    // The offer is moot once the pasted text is gone from the draft (the draft still
    // reads as it was before the paste on the render the offer is raised in).
    if (longPaste && text !== longPaste.before && !text.includes(longPaste.pasted.trim().slice(0, 200))) setLongPaste(null);
  }, [longPaste, text]);

  const takeFiles = React.useCallback((files) => {
    const list = Array.from(files ?? []).filter((f) => f && (f.size > 0 || f.name));
    if (list.length === 0) return false;
    if (allowImages && list.every((f) => safeString(f.type).startsWith('image/'))) {
      // Images go with the message as before (the server keeps them on the session too).
      for (const file of list) {
        readPastedImage(file).then((dataUrl) => {
          if (dataUrl) onAddImage({ id: newPromptImageId(), dataUrl });
        });
      }
      return true;
    }
    if (!allowFiles) return false;
    onAttachFiles(list);
    return true;
  }, [allowFiles, allowImages, onAddImage, onAttachFiles]);
  // A message may be a screenshot alone: the send path substitutes a placeholder text.
  const canSend = !disabled && (!accentUtils.isEmpty(text) || hasImages);
  const hasIntentToggle = !accentUtils.isEmpty(safeString(askLabel));

  // Sticky on purpose: a user asking several questions in a row should not have to
  // re-select the mode before every message. 'change' is the usual default because
  // most prompts are change requests, and 'ask' turns are server-enforced read-only
  // anyway; hosts where asking dominates opt into 'ask' via defaultIntent.
  const [intentMode, setIntentMode] = React.useState(defaultIntent === 'ask' ? 'ask' : 'change');

  const sendBtnRef = React.useRef(null);
  // Set just before a programmatic click so the button's onClick sends the supplied
  // text/intent instead of the textarea content. A ref (not state) so the pending
  // send is visible to the click handler in the same tick, without a re-render.
  const pendingSendRef = React.useRef(null);

  React.useImperativeHandle(ref, () => ({
    send: (sendText, intent) => {
      pendingSendRef.current = { text: safeString(sendText), intent };
      sendBtnRef.current?.click();
    },
  }));

  const sendWithIntent = async (e, sendText, intent) => {
    try {
      await onSend?.({ text: sendText, intent });
    } finally {
      if (e?.promise) e.promise();
    }
  };

  // Paste: attached to the WRAPPER (paste events bubble up from the textarea). Image
  // items are claimed as screenshots; other files (a file copied in Explorer) go to the
  // session when the host takes files; a plain text paste flows into the textarea
  // untouched — a LONG one additionally raises the "keep it as a session file" offer.
  const onPaste = React.useCallback((e) => {
    if (disabled) return;
    const items = Array.from(e?.clipboardData?.items ?? []);
    const imageItems = items.filter((i) => safeString(i?.type).startsWith('image/'));
    if (imageItems.length > 0 && allowImages) {
      e.preventDefault();
      const files = imageItems.map((i) => i.getAsFile()).filter(Boolean);
      for (const file of files) {
        readPastedImage(file).then((dataUrl) => {
          if (!dataUrl) return;
          // The cap is enforced by the host's add handler too (state may move
          // between paste and decode).
          onAddImage({ id: newPromptImageId(), dataUrl });
        });
      }
      return;
    }
    const files = items.filter((i) => i?.kind === 'file').map((i) => i.getAsFile()).filter(Boolean);
    if (files.length > 0 && allowFiles) {
      e.preventDefault();
      onAttachFiles(files);
      return;
    }
    if (!allowDropText) return;
    const pasted = safeString(e?.clipboardData?.getData?.('text/plain'));
    if (pasted.trim().length >= LONG_PASTE_CHARS) {
      const html = safeString(e?.clipboardData?.getData?.('text/html'));
      setLongPaste({ before: text, pasted, html });
    }
  }, [allowDropText, allowFiles, allowImages, disabled, onAddImage, onAttachFiles, text]);

  const canDrop = (allowFiles || allowImages || allowDropText) && !disabled;
  const onDragEnter = (e) => {
    if (!canDrop || !hasFiles(e.dataTransfer)) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setDragOver(true);
  };
  const onDragOver = (e) => {
    if (!canDrop || !hasFiles(e.dataTransfer)) return;
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'copy'; } catch { /* some browsers refuse */ }
  };
  const onDragLeave = (e) => {
    if (!canDrop || !hasFiles(e.dataTransfer)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragOver(false);
  };
  const onDrop = (e) => {
    dragDepthRef.current = 0;
    setDragOver(false);
    if (!canDrop) return;
    const dt = e.dataTransfer;
    if (hasFiles(dt)) {
      e.preventDefault();
      takeFiles(dt.files);
      return;
    }
    const dropped = safeString(dt?.getData?.('text/plain'));
    if (allowDropText && dropped.trim().length >= LONG_PASTE_CHARS) {
      e.preventDefault();
      onDropText(dropped);
    }
  };

  const keepLongPasteAsFile = () => {
    const offer = longPaste;
    setLongPaste(null);
    if (!offer) return;
    onChange?.(offer.before);
    onDropText(offer.pasted, offer.html);
  };

  // The screen snip: one frame of what the user sees, cropped, in as a pasted image would be.
  const [snipError, setSnipError] = React.useState('');
  const screenSnip = useScreenSnip({
    onCapture: (canvas) => {
      const dataUrl = toPromptImageDataUrl(canvas, canvas.width, canvas.height);
      if (dataUrl) onAddImage?.({ id: newPromptImageId(), dataUrl });
    },
    onError: (err) => setSnipError(safeString(err?.message) || t('The screen could not be captured.')),
  });
  React.useEffect(() => {
    if (!snipError) return undefined;
    const timer = setTimeout(() => setSnipError(''), 8000);
    return () => clearTimeout(timer);
  }, [snipError]);
  const allowSnip = allowImages && screenSnip.supported;

  // Chips: this message's screenshots first (marked), then the session's files. An
  // image the host has kept as a session file (`keptName`) and that file are ONE chip:
  // the file's entry, marked as going with the message, with the sharper local
  // thumbnail; the image-only chip stands while the file is not on the list yet.
  const stripEntries = React.useMemo(() => {
    if (!hasStrip) return [];
    const merged = new Map(); // attachment id -> the message image it carries
    const imageChips = [];
    imageList.forEach((img, i) => {
      const keptName = safeString(img.keptName).toLowerCase();
      const file = keptName ? attachmentList.find((a) => safeString(a.name).toLowerCase() === keptName && !merged.has(a.id)) : null;
      if (file) {
        merged.set(file.id, img);
        return;
      }
      imageChips.push({
        id: `msg:${img.id}`,
        imageId: img.id,
        name: safeString(img.keptName) || t('Screenshot {{n}}', { n: i + 1 }),
        kind: 'image',
        status: 'ready',
        thumb: img.dataUrl,
        withMessage: true,
        removable: typeof onRemoveImage === 'function',
      });
    });
    const files = attachmentList.map((a) => {
      const img = merged.get(a.id);
      return img ? { ...a, imageId: img.id, withMessage: true, thumb: a.thumb || img.dataUrl, messageDataUrl: img.dataUrl } : a;
    });
    return [...imageChips, ...files];
  }, [attachmentList, hasStrip, imageList, onRemoveImage]);

  // × on a chip that goes with the message: the image off the message, and the session
  // file with it when the chip is the file's — one chip, one gesture.
  const removeStripEntry = React.useCallback((a) => {
    if (a?.imageId) onRemoveImage?.(a.imageId);
    if (a && !safeString(a.id).startsWith('msg:')) onRemoveAttachment?.(a);
  }, [onRemoveAttachment, onRemoveImage]);

  const openStripEntry = React.useCallback((a) => {
    if (!a) return;
    if (safeString(a.id).startsWith('msg:')) { openDataUrl(a.thumb); return; }
    onOpenAttachment?.(a);
  }, [onOpenAttachment]);

  // Mark up: the chip's picture into the drawing dialog; what comes back goes on the
  // message as a new image would (through the host's add path, so a host that keeps
  // images as files keeps the marked one). An image of THIS message is replaced — the
  // unmarked one off the message and, when it is a file of the session's, off the
  // session; a file from before stays as it is beside the marked copy.
  const markUpStripEntry = React.useCallback(async (a) => {
    const src = a?.messageDataUrl || a?.thumb;
    if (!src || !allowImages) return;
    const res = await showImageMarkup(src);
    if (!res?.canvas) return;
    const dataUrl = toPromptImageDataUrl(res.canvas, res.canvas.width, res.canvas.height);
    if (!dataUrl) return;
    if (a.imageId) {
      onRemoveImage?.(a.imageId);
      if (!safeString(a.id).startsWith('msg:')) onRemoveAttachment?.(a);
    }
    onAddImage({ id: newPromptImageId(), dataUrl });
  }, [allowImages, onAddImage, onRemoveAttachment, onRemoveImage]);

  const whatGoesHere = allowFiles
    ? (allowSnip ? t('Paste or snip a screenshot, drop a file or use the paperclip — the assistant reads them.') : t('Paste a screenshot, drop a file or use the paperclip — the assistant reads them.'))
    : (allowSnip ? t('Paste or snip a screenshot to send it with your message.') : t('Paste a screenshot to send it with your message.'));

  // The hint line says what matters now: a snip that failed, a file being read, a held
  // one waiting for the go-ahead, otherwise what can be dropped here.
  const hint = React.useMemo(() => {
    if (snipError) return snipError;
    if (!hasStrip) return safeString(imageHint) || (allowImages ? whatGoesHere : '');
    const working = attachmentList.filter((a) => a.status === ATTACHMENT_STATUS_UPLOADING || a.status === 'pending' || a.status === 'processing');
    if (working.length === 1) return describeAttachment(working[0]);
    if (working.length > 1) return t('Reading {{count}} files…', { count: working.length });
    const held = attachmentList.find((a) => a.status === 'held');
    if (held) return t('"{{name}}" is large — click Read it on the chip when you want it read.', { name: held.name });
    const failed = attachmentList.find((a) => a.status === 'failed');
    if (failed) return describeAttachment(failed);
    if (safeString(imageHint)) return imageHint;
    return whatGoesHere;
  }, [allowImages, attachmentList, hasStrip, imageHint, snipError, whatGoesHere]);

  const showDropZone = canDrop && (dragOver || !!dropActive);

  return (
    <div
      style={{ display: 'grid', gap: 8, position: 'relative' }}
      onPaste={onPaste}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-tagname="promptComposer"
    >
      {showDropZone && (
        <div
          data-tagname="promptDropZone"
          style={{
            position: 'absolute', inset: -4, zIndex: 5, borderRadius: 8, border: '2px dashed #6f42c1', background: 'rgba(111, 66, 193, 0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', color: '#3b2a6b', fontSize: 13, fontWeight: 600,
          }}
        >
          {t('Drop to add to this session')}
        </div>
      )}
      <AccentTemplateTextAreaUnbound
        label={label ?? t('Prompt')}
        rows={rows}
        max={max}
        disabled={!!disabled}
        placeholder={placeholder}
        templateCatalog={templateCatalog}
        insertMode="snippet"
        defaultValue={text}
        onChange={onChange}
        onKeyDown={(e) => {
          const key = e?.key ?? e?.nativeEvent?.key;
          const isEnter = key === 'Enter';
          // e.repeat guards a held-down Enter: auto-repeat would queue a second click
          // before the first send disables the button.
          const isRepeat = !!(e?.repeat ?? e?.nativeEvent?.repeat);
          const allowSubmit = isEnter && !isRepeat && !e?.shiftKey && !e?.altKey && !e?.ctrlKey && !e?.metaKey && !e?.isComposing;
          if (!allowSubmit) return;

          if (e?.preventDefault) e.preventDefault();
          if (!canSend) return;

          // Enter routes through the Send button (not onSend directly) so it gets the
          // identical click flow: self-disable, the double-send debounce and the
          // built-in spinner for the whole round-trip. The button's onClick reads the
          // textarea text and the SELECTED mode on intent-toggle hosts (legacy
          // single-button hosts keep sending without an intent — the AI infers).
          sendBtnRef.current?.click();
        }}
        style={{ width: '100%' }}
      />
      {hasStrip && stripEntries.length > 0 && (
        <AssistantAttachmentStrip
          attachments={stripEntries}
          disabled={!!disabled}
          onRemove={removeStripEntry}
          onKeep={onKeepAttachment}
          onConfirm={onConfirmAttachment}
          onOpen={openStripEntry}
          onMarkup={allowImages ? markUpStripEntry : undefined}
        />
      )}
      {screenSnip.overlay}
      {longPaste && (
        <div
          data-tagname="longPasteOffer"
          role="status"
          style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '4px 8px' }}
        >
          <span style={{ flex: 1 }}>{t('Long paste — keep it as a session file instead? The assistant reads it the same way, and your message stays short.')}</span>
          <button
            type="button"
            data-tagname="longPasteKeep"
            onClick={keepLongPasteAsFile}
            style={{ border: '1px solid #f59e0b', background: '#fff', color: '#92400e', borderRadius: 10, fontSize: 11, fontWeight: 600, padding: '1px 8px', cursor: 'pointer' }}
          >
            {t('Keep as file')}
          </button>
          <button type="button" title={t('Keep it in the message')} onClick={() => setLongPaste(null)} style={{ border: 'none', background: 'none', padding: 0, lineHeight: 0, color: '#92400e', cursor: 'pointer' }}>
            <LuX size={12} />
          </button>
        </div>
      )}
      {hasImages && !hasStrip && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} data-tagname="promptImages">
          {imageList.map((img) => (
            <div key={img.id} style={{ position: 'relative', display: 'inline-block' }}>
              <img
                src={img.dataUrl}
                alt={t('Screenshot')}
                title={t('Goes with your message — click to mark it up')}
                onClick={() => !disabled && markUpStripEntry({ id: `msg:${img.id}`, imageId: img.id, thumb: img.dataUrl })}
                style={{ height: 48, borderRadius: 4, border: '1px solid #dee2e6', display: 'block', cursor: disabled ? 'default' : 'pointer' }}
              />
              <button
                type="button"
                title={t('Remove screenshot')}
                disabled={!!disabled}
                onClick={() => onRemoveImage?.(img.id)}
                style={{
                  position: 'absolute', top: -6, right: -6, width: 16, height: 16,
                  borderRadius: '50%', border: '1px solid #dee2e6', background: '#fff',
                  color: '#b71c1c', fontSize: 10, lineHeight: '13px', padding: 0,
                  cursor: 'pointer', fontWeight: 700,
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
        {allowFiles && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={acceptFiles}
              style={{ display: 'none' }}
              data-tagname="promptAttachInput"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                e.target.value = '';
                if (files.length > 0) takeFiles(files);
              }}
            />
            <button
              type="button"
              data-tagname="promptAttach"
              title={t('Attach a file to this session (PDF, image, spreadsheet, csv, text…)')}
              disabled={!!disabled}
              onClick={() => fileInputRef.current?.click()}
              style={{ border: 'none', background: 'none', padding: 2, lineHeight: 0, color: disabled ? '#adb5bd' : '#6f42c1', cursor: disabled ? 'default' : 'pointer' }}
            >
              <LuPaperclip size={15} />
            </button>
          </>
        )}
        {allowSnip && (
          <button
            type="button"
            data-tagname="promptSnip"
            title={t('Snip part of the screen into your message — the browser asks what to share, then drag over the part to send')}
            disabled={!!disabled || screenSnip.busy || imageList.length >= MAX_PROMPT_IMAGES}
            onClick={() => { setSnipError(''); screenSnip.start(); }}
            style={{ border: 'none', background: 'none', padding: 2, lineHeight: 0, color: disabled || screenSnip.busy ? '#adb5bd' : '#6f42c1', cursor: disabled ? 'default' : 'pointer' }}
          >
            <LuCamera size={15} />
          </button>
        )}
        {atMax ? (
          <div style={{ marginRight: 'auto', fontSize: 10, color: '#b71c1c', opacity: 0.85 }} data-tagname="promptAtMax">
            {t('{{max}} character limit reached - pasted text beyond it was cut off.', { max: max.toLocaleString() })}
          </div>
        ) : !!hint && (
          <div style={{ marginRight: 'auto', fontSize: 10, opacity: snipError ? 0.9 : 0.6, color: snipError ? '#b71c1c' : undefined }} data-tagname="promptHint">
            {hint}
          </div>
        )}
        {hasIntentToggle && (
          // Plain reactstrap buttons: AccentButton's click debounce/self-disable suits
          // actions, not a mode switch that must flip instantly.
          <div className="btn-group" role="group" data-tagname="promptIntentToggle">
            <Button
              size="sm"
              type="button"
              color="primary"
              outline={intentMode !== 'ask'}
              active={intentMode === 'ask'}
              disabled={!!disabled}
              onClick={() => setIntentMode('ask')}
            >
              {askLabel}
            </Button>
            <Button
              size="sm"
              type="button"
              color="primary"
              outline={intentMode !== 'change'}
              active={intentMode === 'change'}
              disabled={!!disabled}
              onClick={() => setIntentMode('change')}
            >
              {safeString(sendLabel) || t('Send')}
            </Button>
          </div>
        )}
        {/* No disabled prop on purpose: a controlled disabled prop makes AccentButton
            drop its internal click state on any parent re-render, which kills the
            built-in spinner mid-send. The guard below no-ops instead. */}
        <AccentButton
          ref={sendBtnRef}
          tagName="promptSend"
          onClick={async (e) => {
            const pending = pendingSendRef.current;
            pendingSendRef.current = null;
            const sendText = pending ? pending.text : text;
            const sendIntent = pending ? pending.intent : (hasIntentToggle ? intentMode : undefined);
            if (disabled || (accentUtils.isEmpty(sendText) && !hasImages)) {
              e.promise();
              return;
            }
            await sendWithIntent(e, sendText, sendIntent);
          }}
        >
          {hasIntentToggle ? t('Send') : (safeString(sendLabel) || t('Send'))}
        </AccentButton>
      </div>
    </div>
  );
});
